/**
 * Neon Auth Service
 * Service untuk menghandle authentication menggunakan Neon Auth (Better Auth)
 * Neon Auth adalah managed authentication service yang built on Better Auth
 * 
 * Better Auth REST API endpoints:
 * - POST /sign-in/email - Sign in with email/password
 * - POST /sign-up/email - Sign up with email/password
 * - GET /get-session - Get current session
 * - POST /sign-out - Sign out
 * - POST /verify-email - Verify email with token
 * - POST /send-verification-email - Send/resend verification email
 */

import config from '../config/appConfig';

// Neon Auth base URL - constructed from project endpoint
// Pattern: https://{endpoint-id}.neonauth.{region}.aws.neon.tech/{database}/auth
const getNeonAuthUrl = () => {
  // Check environment variable first
  const envUrl = process.env.REACT_APP_NEON_AUTH_URL;
  if (envUrl) {
    console.log('[NeonAuthService] Using env URL:', envUrl);
    return envUrl;
  }
  
  // Default pattern based on endpoint_id from project config
  const endpointId = 'ep-gentle-snow-a15tafb2';
  const region = 'ap-southeast-1';
  const database = 'neondb';
  const defaultUrl = `https://${endpointId}.neonauth.${region}.aws.neon.tech/${database}/auth`;
  console.log('[NeonAuthService] Using default URL:', defaultUrl);
  return defaultUrl;
};

const NEON_AUTH_BASE_URL = getNeonAuthUrl();

class NeonAuthService {
  constructor() {
    this.baseURL = NEON_AUTH_BASE_URL;
    console.log('[NeonAuthService] Initialized with URL:', this.baseURL);
  }

  /**
   * Get full API endpoint URL
   */
  getEndpoint(path) {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseURL}${cleanPath}`;
  }

  /**
   * Normalize user object - handle both camelCase and snake_case
   */
  normalizeUser(user) {
    if (!user) return null;
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      image: user.image,
      // Handle both emailVerified and email_verified
      emailVerified: user.emailVerified ?? user.email_verified ?? false,
      createdAt: user.createdAt || user.created_at,
      updatedAt: user.updatedAt || user.updated_at,
    };
  }

  /**
   * Make request to Neon Auth API
   */
  async request(endpoint, options = {}) {
    const url = this.getEndpoint(endpoint);
    
    console.log(`[NeonAuthService] ${options.method || 'GET'} ${url}`);

    const requestConfig = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Important for session cookies
    };

    try {
      const response = await fetch(url, requestConfig);
      
      // Get response body
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          data = { message: text };
        }
      }

      console.log(`[NeonAuthService] Response ${response.status}:`, data);

      if (!response.ok) {
        const errorMsg = data.message || data.error || data.code || `HTTP ${response.status}`;
        const error = new Error(errorMsg);
        error.response = { data, status: response.status };
        error.code = data.code;
        throw error;
      }

      return data;
    } catch (error) {
      // Handle network/CORS errors
      if (error.name === 'TypeError') {
        console.error('[NeonAuthService] Network error:', error.message);
        const networkError = new Error('Tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
        networkError.name = 'NetworkError';
        networkError.originalError = error;
        throw networkError;
      }
      console.error('[NeonAuthService] Request error:', error);
      throw error;
    }
  }

  /**
   * Sign in with email and password
   * Better Auth endpoint: POST /sign-in/email
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Auth response with user and session
   */
  async signIn(email, password) {
    try {
      console.log('[NeonAuthService] Signing in user');
      
      if (!email || !password) {
        throw new Error('Email dan password harus diisi');
      }
      
      const response = await this.request('/sign-in/email', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      console.log('[NeonAuthService] Sign in response:', JSON.stringify(response));

      // Better Auth returns { token, user, redirect } format
      const user = this.normalizeUser(response.user);
      const accessToken = response.token || response.accessToken || response.session?.token;
      
      // Check if email is verified (config requires verification)
      if (user && !user.emailVerified) {
        console.log('[NeonAuthService] User email not verified');
        const error = new Error('Email Anda belum diverifikasi. Silakan cek inbox dan klik link verifikasi.');
        error.code = 'EMAIL_NOT_VERIFIED';
        error.user = user;
        error.email = email;
        throw error;
      }
      
      console.log('[NeonAuthService] Sign in successful:', { 
        hasUser: !!user, 
        hasToken: !!accessToken,
        emailVerified: user?.emailVerified 
      });
      
      return {
        user,
        session: response.session || { token: accessToken },
        access_token: accessToken,
        expires_in: 3600, // 1 hour default
      };
    } catch (error) {
      // Check for specific error codes from Better Auth
      if (error.code === 'INVALID_EMAIL_OR_PASSWORD' || error.message?.includes('Invalid email or password')) {
        const authError = new Error('Email atau password salah. Silakan coba lagi.');
        authError.code = 'INVALID_CREDENTIALS';
        throw authError;
      }
      if (error.code === 'EMAIL_NOT_VERIFIED') {
        throw error;
      }
      console.error('[NeonAuthService] Sign in error:', error);
      throw error;
    }
  }

  /**
   * Sign up with email and password
   * Better Auth endpoint: POST /sign-up/email
   * When email verification is enabled, verification email is sent automatically
   * @param {Object} userData - User registration data { email, password, name, phone? }
   * @returns {Promise<Object>} Auth response with user (verification required)
   */
  async signUp(userData) {
    try {
      const { email, password, name } = userData;
      
      console.log('[NeonAuthService] Signing up user');
      
      // Better Auth sign-up payload
      const payload = { email, password };
      if (name) {
        payload.name = name;
      }
      
      const response = await this.request('/sign-up/email', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const session = response.session || {};
      const user = this.normalizeUser(response.user);
      
      // Neon Auth automatically sends verification email when sendVerificationEmailOnSignUp is true
      // User needs to verify email before they can login
      const requiresVerification = user && !user.emailVerified;
      
      // Extract token (may not exist if verification is required)
      const accessToken = session.token || response.token || session.accessToken || response.accessToken;
      const expiresAt = session.expiresAt || response.expiresAt;
      
      console.log('[NeonAuthService] Sign up successful:', { 
        hasUser: !!user, 
        hasToken: !!accessToken,
        requiresVerification 
      });
      
      return {
        user,
        session,
        access_token: accessToken,
        expires_in: expiresAt ? Math.floor((new Date(expiresAt) - new Date()) / 1000) : 3600,
        requiresVerification,
      };
    } catch (error) {
      // Check for specific error codes
      if (error.code === 'USER_ALREADY_EXISTS' || error.message?.includes('already exists') || error.message?.includes('sudah terdaftar')) {
        const authError = new Error('Email sudah terdaftar. Silakan gunakan email lain atau login.');
        authError.code = 'USER_ALREADY_EXISTS';
        throw authError;
      }
      console.error('[NeonAuthService] Sign up error:', error);
      throw error;
    }
  }

  /**
   * Initiate Google OAuth redirect flow
   * Better Auth endpoint: GET /sign-in/social/google
   * This will redirect user to Google OAuth, then back to callback URL
   * @param {string} callbackUrl - URL to redirect to after OAuth completes
   * @returns {void} - Redirects browser, doesn't return
   */
  initiateGoogleOAuth(callbackUrl = null) {
    // Get the callback URL - use current origin + callback path
    const defaultCallbackUrl = callbackUrl || `${window.location.origin}/auth/callback/google`;
    
    // Better Auth OAuth redirect endpoint
    // The callback URL should be configured in Neon Auth settings
    const oauthUrl = this.getEndpoint(`/sign-in/social/google?callbackURL=${encodeURIComponent(defaultCallbackUrl)}`);
    
    console.log('[NeonAuthService] Initiating Google OAuth redirect to:', oauthUrl);
    
    // Redirect browser to OAuth URL
    window.location.href = oauthUrl;
  }

  /**
   * Handle Google OAuth callback
   * Called after user returns from Google OAuth
   * Better Auth sets session cookie, we just need to get the session
   * @returns {Promise<Object>} Auth response with user and session
   */
  async handleGoogleOAuthCallback() {
    try {
      console.log('[NeonAuthService] Handling Google OAuth callback...');
      
      // After OAuth redirect, Better Auth sets session cookie
      // We need to get the session from the cookie
      // Retry logic: Better Auth might need time to set the cookie
      let response = null;
      let retries = 5;
      let delay = 500;
      
      while (retries > 0 && (!response || !response.user)) {
        await new Promise(resolve => setTimeout(resolve, delay));
        response = await this.getSession();
        
        if (response && response.user) {
          break;
        }
        
        retries--;
        delay = Math.min(delay * 1.5, 2000); // Increase delay, max 2 seconds
        console.log(`[NeonAuthService] Retrying session retrieval... (${retries} retries left)`);
      }
      
      if (!response || !response.user) {
        console.error('[NeonAuthService] No user in session after OAuth after all retries');
        throw new Error('Login Google gagal. Sesi tidak ditemukan. Silakan coba lagi.');
      }
      
      console.log('[NeonAuthService] Google OAuth callback successful');
      
      // Ensure we have normalized user data
      const normalizedUser = this.normalizeUser(response.user);
      
      return {
        user: normalizedUser,
        session: response.session,
        access_token: response.access_token,
        expires_in: 3600, // Default 1 hour
      };
    } catch (error) {
      console.error('[NeonAuthService] Google OAuth callback error:', error);
      throw error;
    }
  }

  /**
   * Sign in with Google OAuth (legacy idToken method - kept for backward compatibility)
   * Better Auth endpoint: POST /sign-in/social/google
   * @param {string} idToken - Google ID token
   * @returns {Promise<Object>} Auth response with user and session
   * @deprecated Use initiateGoogleOAuth() for redirect-based flow
   */
  async signInWithGoogle(idToken) {
    try {
      // Better Auth uses /sign-in/social/google endpoint
      const response = await this.request('/sign-in/social/google', {
        method: 'POST',
        body: JSON.stringify({
          idToken,
        }),
      });

      const session = response.session || response;
      const user = response.user || response;
      
      return {
        user: user,
        session: session,
        access_token: session?.accessToken || session?.token || response.accessToken,
        expires_in: session?.expiresAt ? 
          Math.floor((new Date(session.expiresAt) - new Date()) / 1000) : 
          (response.expiresIn || null),
      };
    } catch (error) {
      console.error('[NeonAuthService] Google sign in error:', error);
      throw error;
    }
  }

  /**
   * Get current session/user
   * Better Auth endpoint: GET /get-session
   * @returns {Promise<Object>} Current user and session
   */
  async getSession() {
    try {
      console.log('[NeonAuthService] Getting current session...');
      
      const response = await this.request('/get-session', {
        method: 'GET',
      });

      // Handle null response (no active session)
      if (!response || response === null || (typeof response === 'object' && Object.keys(response).length === 0)) {
        console.log('[NeonAuthService] No active session');
        return {
          user: null,
          session: null,
          access_token: null,
        };
      }

      // Better Auth might return user directly or nested in session
      const user = response.user || response;
      const session = response.session || response;
      
      // Normalize user data
      const normalizedUser = this.normalizeUser(user);
      
      // Extract access token from various possible locations
      const accessToken = session?.token || 
                         session?.accessToken || 
                         response.token || 
                         response.accessToken ||
                         session?.access_token ||
                         response.access_token;
      
      console.log('[NeonAuthService] Session retrieved:', { 
        hasUser: !!normalizedUser, 
        hasToken: !!accessToken,
        userEmail: normalizedUser?.email
      });
      
      return {
        user: normalizedUser,
        session: session,
        access_token: accessToken,
      };
    } catch (error) {
      console.error('[NeonAuthService] Get session error:', error);
      // Return empty session instead of throwing for initialization
      return {
        user: null,
        session: null,
        access_token: null,
      };
    }
  }

  /**
   * Sign out
   * Better Auth endpoint: POST /sign-out
   * @returns {Promise<void>}
   */
  async signOut() {
    try {
      await this.request('/sign-out', {
        method: 'POST',
      });
    } catch (error) {
      console.error('[NeonAuthService] Sign out error:', error);
      // Don't throw error on sign out failure
    }
  }

  /**
   * Refresh session/token
   * Better Auth endpoint: POST /session/refresh or just get-session again
   * @returns {Promise<Object>} New session data
   */
  async refreshSession() {
    try {
      console.log('[NeonAuthService] Refreshing session...');
      
      // Try to refresh using the session endpoint
      // Better Auth might use /session/refresh or just re-fetching session works
      const response = await this.getSession();
      
      console.log('[NeonAuthService] Session refreshed');
      
      return {
        user: response.user,
        session: response.session,
        access_token: response.access_token,
        expires_in: 3600, // Default 1 hour
      };
    } catch (error) {
      console.error('[NeonAuthService] Refresh session error:', error);
      throw error;
    }
  }

  /**
   * Send email verification link
   * Better Auth endpoint: POST /send-verification-email
   * @param {string} email - User email
   * @returns {Promise<Object>} Response
   */
  async sendVerificationEmail(email) {
    try {
      console.log('[NeonAuthService] Sending verification email');
      
      const response = await this.request('/send-verification-email', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      console.log('[NeonAuthService] Verification email sent');
      return response;
    } catch (error) {
      console.error('[NeonAuthService] Send verification email error:', error);
      throw error;
    }
  }

  /**
   * Verify email with token
   * Better Auth endpoint: GET /verify-email (with token as query param)
   * @param {string} token - Verification token from email link
   * @returns {Promise<Object>} Auth response with user and session
   */
  async verifyEmail(token) {
    try {
      console.log('[NeonAuthService] Verifying email with token...');
      
      // Better Auth verify-email can be GET with token query or POST with token body
      // Try GET first (more common for email link verification)
      const response = await this.request(`/verify-email?token=${encodeURIComponent(token)}`, {
        method: 'GET',
      });

      const session = response.session || {};
      const user = this.normalizeUser(response.user);
      const accessToken = session.token || response.token || session.accessToken || response.accessToken;
      const expiresAt = session.expiresAt || response.expiresAt;
      
      console.log('[NeonAuthService] Email verified successfully');
      
      return {
        user,
        session,
        access_token: accessToken,
        expires_in: expiresAt ? Math.floor((new Date(expiresAt) - new Date()) / 1000) : 3600,
      };
    } catch (error) {
      // If token is invalid or expired
      if (error.message?.includes('invalid') || error.message?.includes('expired')) {
        const verifyError = new Error('Token verifikasi tidak valid atau sudah kadaluarsa. Silakan minta link verifikasi baru.');
        verifyError.code = 'INVALID_TOKEN';
        throw verifyError;
      }
      console.error('[NeonAuthService] Verify email error:', error);
      throw error;
    }
  }

  /**
   * Resend verification email
   * Better Auth endpoint: POST /send-verification-email
   * @param {string} email - User email
   * @returns {Promise<Object>} Response
   */
  async resendVerificationEmail(email) {
    try {
      console.log('[NeonAuthService] Resending verification email');
      
      // Better Auth uses same endpoint for send and resend
      const response = await this.request('/send-verification-email', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      console.log('[NeonAuthService] Verification email resent');
      return response;
    } catch (error) {
      // Check for rate limiting
      if (error.message?.includes('rate') || error.code === 'RATE_LIMITED') {
        const rateLimitError = new Error('Terlalu banyak permintaan. Silakan tunggu beberapa menit sebelum mencoba lagi.');
        rateLimitError.code = 'RATE_LIMITED';
        throw rateLimitError;
      }
      console.error('[NeonAuthService] Resend verification email error:', error);
      throw error;
    }
  }
}

// Create singleton instance
const neonAuthService = new NeonAuthService();

export default neonAuthService;
